import { cloneDeep } from 'lodash';
import { getFramework } from '@/framework';
import type { Cell, ITableLayer } from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import {
    DEFAULT_TABLE_CELL__TEXT_ALIGN,
    DEFAULT_TABLE_CELL__TEXT_COLOR,
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
} from '../default-configs';
import { TABLE_CONTROLLER_EVENTS } from '../plugins/edit/events';
import tablePresets from '../presets';
import addOrUpdateLayer from '../utils/add-or-update-layer';
import fixTableLayout from '../utils/fix-table-layout';
import { getActiveTableLayer } from '../utils/table';
import { getCellsActions } from './cells-actions';
import { getRowOrColActions } from './row-col-actions';

export const getActiveTableLayerActions = (frameworkIdentity: string) => {
    const framework = getFramework(frameworkIdentity);
    let mouseWheelInfoRef: {
        /** 未滚动前的图层数据 */
        originalLayer: ITableLayer;
        /** 滚动过程中最新的图层数据 (必须存起来，不能getActiveTableLayer, 否则连续调用数据会对不上) */
        newLayer: ITableLayer;
    } | null = null;

    const setRowOrColCount = ({
        mode,
        count,
        isMouseWheel = false,
    }: {
        mode: 'row' | 'col';
        count: number;
        /** 设置行列数是可拖拽的, 拖拽过程中不应该写入历史记录, 需要在拖拽结束后手动调用writeHistoryAfterSetRowOrColCount */
        isMouseWheel?: boolean;
    }) => {
        const preLayer = isMouseWheel
            ? (mouseWheelInfoRef?.newLayer ?? getActiveTableLayer(frameworkIdentity))
            : getActiveTableLayer(frameworkIdentity);
        if (!preLayer) return;

        const nowCount = preLayer.data.baseCells[mode === 'row' ? 'vertical' : 'horizontal'].length;
        const actionType = count > nowCount ? 'add' : 'del';
        const action =
            actionType === 'add'
                ? mode === 'row'
                    ? getRowOrColActions(frameworkIdentity).addRow
                    : getRowOrColActions(frameworkIdentity).addCol
                : mode === 'row'
                  ? getRowOrColActions(frameworkIdentity).delRow
                  : getRowOrColActions(frameworkIdentity).delCol;
        const actionCount = Math.abs(count - nowCount);

        if (actionCount === 0) return;

        const originalMouseWheelLayer = isMouseWheel && !mouseWheelInfoRef ? cloneDeep(preLayer) : null;
        if (isMouseWheel && !mouseWheelInfoRef) {
            framework.service.events.emit(TABLE_CONTROLLER_EVENTS.START_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT);
        }

        let newTableLayer =
            action({
                tableLayer: preLayer,
                targetIndex: actionType === 'add' ? nowCount : nowCount - actionCount,
                actionCount,
            }) ?? preLayer;

        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: true,
        });
        if (isMouseWheel) {
            if (!mouseWheelInfoRef && originalMouseWheelLayer) {
                mouseWheelInfoRef = {
                    originalLayer: originalMouseWheelLayer,
                    newLayer: newTableLayer,
                };
            } else if (mouseWheelInfoRef) {
                mouseWheelInfoRef.newLayer = newTableLayer;
            }
        }
        framework.service.frame.setActiveLayer(newTableLayer.id);
        if (!isMouseWheel) {
            framework.service.history.write(cloneDeep(preLayer), newTableLayer);
        }
    };

    const afterMouseWheelSetRowOrColCount = () => {
        framework.service.events.emit(TABLE_CONTROLLER_EVENTS.END_MOUSE_WHEEL_SET_ROW_OR_COL_COUNT);
        if (!mouseWheelInfoRef) return;
        framework.service.history.write(cloneDeep(mouseWheelInfoRef.originalLayer), mouseWheelInfoRef.newLayer);
        mouseWheelInfoRef = null;
    };

    /** 选择切换样式，仅切换该表格样式下的：单元格颜色、边框 */
    const overwritePreset = async (presetId: string) => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return;
        const preset = tablePresets.find((p) => p.id === presetId);
        if (!preset) return;

        const presetDemo = preset.create();
        const oldAllStyleId = activeTableLayer.data.allStyleId;

        let newTableLayer = cloneDeep(activeTableLayer);
        const cells = newTableLayer.data.cells.flat().filter(Boolean) as Cell[];

        let nextStyleId = `${Math.max(0, ...Object.keys(activeTableLayer.data.styles).map(Number)) + 1}`;
        const getAndUseNextStyleId = () => {
            const styleId = nextStyleId;
            nextStyleId = `${+nextStyleId + 1}`;
            return styleId;
        };

        // 清理无用的backgroundColor和border数据, 防止不断累积数据越来越大
        {
            for (const styleId in newTableLayer.data.styles) {
                const styleItem = newTableLayer.data.styles[styleId];
                delete styleItem.backgroundColor;
                delete styleItem.border;
                if (Object.keys(styleItem).length === 0) {
                    delete newTableLayer.data.styles[styleId];
                    cells.forEach((c) => (c.styleIds = c.styleIds.filter((id) => id !== styleId)));
                }
            }
        }

        // allStyleId变之前, 需要把默认样式保存到每个单元格中, 防止allStyle导致样式改变
        // 原因: 保持allStyleId与新preset的allStyleId一致, 又要保证表格除颜色、边框外的样式一致
        {
            const defaultStyleId = getAndUseNextStyleId();
            newTableLayer.data.styles[defaultStyleId] = {
                bold: false,
                color: DEFAULT_TABLE_CELL__TEXT_COLOR,
                disableAutoWrap: false,
                fontFamily: DEFAULT_FONT_FAMILY().name,
                fontSize: DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
                italic: false,
                lineThrough: false,
                textAlign: DEFAULT_TABLE_CELL__TEXT_ALIGN,
                underline: false,
                verticalAlign: DEFAULT_TABLE_CELL__VERTICAL_ALIGN,
            };
            cells.forEach((c) => {
                if (newTableLayer.data.styles[oldAllStyleId]) {
                    // allStyleId变之前, 需要把allStyle保存到每个单元格中, 防止样式丢失
                    c.styleIds.unshift(oldAllStyleId);
                }
                c.styleIds.unshift(defaultStyleId);
            });
        }

        newTableLayer.data.presetId = presetId;
        const newAllStyleId = getAndUseNextStyleId();
        newTableLayer.data.allStyleId = newAllStyleId;
        newTableLayer.data.styles[newAllStyleId] = presetDemo.data.styles[presetDemo.data.allStyleId];
        newTableLayer = preset.overwriteStyle(newTableLayer);

        newTableLayer = await fixTableLayout({
            originalLayer: newTableLayer,
        });

        newTableLayer = addOrUpdateLayer({
            frameworkIdentity,
            layer: newTableLayer,
            resetMaterialIds: true,
        });
        framework.service.frame.setActiveLayer(newTableLayer.id);
        framework.service.history.write(cloneDeep(activeTableLayer), newTableLayer);
    };

    const getScreenshotUrl_base64 = async (): Promise<string | null> => {
        const activeTableLayer = getActiveTableLayer(frameworkIdentity);
        if (!activeTableLayer) return null;
        const canvas =
            await getFramework(frameworkIdentity).service.behavior.get(activeTableLayer)!.getLayerImageAsync!(
                activeTableLayer,
            );
        return canvas.toDataURL();
    };

    return {
        setRowOrColCount,
        afterMouseWheelSetRowOrColCount,
        overwritePreset,
        allCellActions: getCellsActions({
            frameworkIdentity: frameworkIdentity,
            mode: 'allCells',
        }),
        selectedCellsActions: getCellsActions({
            frameworkIdentity: frameworkIdentity,
            mode: 'selectedCells',
        }),
        getScreenshotUrl_base64,
    };
};
